// =========================================================================
// What the custody buttons say when the server will not do what was asked —
// the operator-executable flow's refusals, as sentences (17 Oct 2026).
//
// Same pattern as lib/cosign-errors.ts: the server answers with a reason word
// (`no_open_window`, `destination_not_approved`, …), and ONE place turns each
// into a sentence a person can act on. An unknown reason is shown as it came:
// a refusal nobody planned for must not be softened into a guess. The
// signature sentences follow the signing page's own (cosign/src/notices.ts),
// so the operator hears one voice whichever door he signed through.
// =========================================================================

import { ApiError } from '../../lib/errors';
import { CLASS_WORD, groupDigits } from '../../lib/custody-format';
import type { PayoutSource } from '../../lib/api';
import { fmtTime } from '../../lib/format';

/** The reason, wherever a message carries it: the raw body, or main's ApiError wording. */
function reasonIn(message: string, known: Record<string, string>): string | null {
  const quoted = /"reason"\s*:\s*"([^"]+)"/.exec(message)?.[1];
  if (quoted) return quoted;
  return Object.keys(known).find((key) => new RegExp(`\\b${key}\\b`).test(message)) ?? null;
}

const pick = (known: Record<string, string>, err: unknown): string => {
  const reason = err instanceof ApiError
    ? (typeof err.body.reason === 'string' ? err.body.reason : null)
    : reasonIn(String((err as { message?: unknown } | null)?.message ?? err), known);
  const sentence = reason !== null && Object.hasOwn(known, reason) ? known[reason] : undefined;
  return sentence ?? String((err as { message?: unknown } | null)?.message ?? err);
};

// ---- moves ---------------------------------------------------------------

/** The same move already open and the desk names no status (an older Worker): no claim about a signature. */
const ALREADY_OPEN_NEUTRAL = 'A move like this is already open — see Approvals, or Deposits for its progress.';

const MOVE: Record<string, string> = {
  move_already_waiting: ALREADY_OPEN_NEUTRAL,
  no_open_window:
    'Nobody is at the desk: open an approval window first, then queue the move.',
  destination_not_found:
    'That address is not in the register, or it belongs to another entity. Refresh the list and pick it again.',
  destination_not_approved:
    'This payout address is not approved in the Books, so nothing can be sent to it yet. Approve it there first.',
  destination_not_active:
    'This address is retired or archived. It cannot be paid.',
  destination_not_merchant:
    'Merchant Hot and Flagged pay only a party the Books mark merchant. Pay this address from Hot.',
  request_voided:
    'This press was closed. Pay again as a new payment.',
  move_failed:
    'That payment failed on the desk — start a new one.',
  release_pending:
    'A release from Flagged is still open — one at a time. Sign it in Approvals, or wait for it to complete.',
  // The deal a payout settles (the wallet's P-063, db/0111, 3 Nov 2026).
  deal_invalid: 'That deal reference is not valid. Choose it again.',
  deal_not_a_payout: 'Only a payout to a customer names a deal.',
  deal_not_found: 'No such deal in the Books.',
  deal_not_payees: 'That deal is another customer\'s.',
  deal_voided: 'That deal was voided.',
  deal_unavailable: 'Naming a deal is not switched on yet. Pay with no deal.',
  // Flagged back to Hot (the wallet's item 5, db/0109, 3 Nov 2026).
  held_for_desk_money:
    'The desk\'s own money names no depositor. Press Move again.',
  desk_return_unavailable:
    'Flagged back to Hot is not switched on yet.',
  destination_not_payable:
    'A move never pays this kind of wallet: a Deposit address, Fee, or Hot itself.',
  destination_wrong_chain:
    'That address is on another chain. Only TRON USDT moves today.',
  amount_invalid:
    'The amount was not accepted: plain digits, a dot for the decimals, at most six places, above zero.',
  from_not_supported:
    'Only Hot pays out today.',
  destination_id_invalid:
    'The address was not named properly. Refresh the list and pick it again.',
  same_wallet:
    'A wallet cannot pay itself.',
  over_move_cap:
    'Over the per-move cap. Raise it on the Flow screen, or move less.',
  over_day_cap:
    'Over the daily cap with today\'s moves. Raise it on the Flow screen, or wait for tomorrow.',
  limits_unset:
    'No caps are set, so nothing moves. Set them on the Flow screen.',
  over_first_payout_cap:
    'A first payout to a new address is capped until one completes. Send less, or raise the first-payout cap on the Flow screen.',
  first_payout_pending:
    'A first payout to this address is still open. A second one waits until it completes.',
  // The whitelist's test (db/0098); the server's line carries the figure and is said instead when present.
  test_amount:
    'The first payment to this address is the 10 USDT test, exactly.',
  test_pending:
    'The 10 USDT test to this address is on its way; nothing else until it lands and you press Received.',
  test_not_received:
    'The test landed — confirm with the customer, then press Received on Payout addresses.',
};

/** Why Received was not taken (db/0098) — one line; the server's word behind Details. */
const RECEIVED: Record<string, string> = {
  test_not_landed: 'The test has not landed yet — try again once it has.',
  not_approved: 'The Books no longer approve this address.',
  payout_address: 'No such payout address.',
};
export function receivedRefusal(err: unknown): string {
  const reason = err instanceof ApiError && typeof err.body.reason === 'string' ? err.body.reason : null;
  return (reason !== null && Object.hasOwn(RECEIVED, reason) ? RECEIVED[reason] : undefined) ?? "Couldn't save Received — try again.";
}

/** What a short source can be filled from — the recovery that can work, by source (Screens' sources review, 23 Oct 2026). */
const FILL: Record<PayoutSource, string> = {
  running_hot: 'Move less, or wait for a deposit to be swept in.',
  dirty_hot: 'Move less, or wait for a merchant deposit to be routed in.',
  frozen: 'Move less — Flagged holds only what you held there.',
  treasury: 'Move less, or fund the Treasury first.',
};
/** The refusals that name the source: its own word, never Hot's for another. */
function sourced(from: PayoutSource): Record<string, string> {
  const word = CLASS_WORD[from] ?? from;
  return {
    no_source_wallet: `The desk has no active ${word} on this chain, so there is nothing to pay from.`,
    insufficient_balance: `${word} does not hold that much at its last reading. Check Balances, or move less.`,
    balance_stale: `${word}'s balance reading is too old to move on. Is the desk running?`,
    balance_unread: `${word}'s balance has never been read. Is the desk running?`,
    source_cannot_pay_fee: `${word} holds too little TRX to pay one transfer's fee. The desk refills it from the fee wallet — try again in a few minutes, or top it up.`,
  };
}

/**
 * The same move already open, by where it stands (the operator's pass, 23 Oct
 * 2026: a payout he had signed and the desk had sent was said to be "waiting
 * for your signature" while Approvals showed nothing). "Waiting for your
 * signature" is said only of an unsigned card; one in flight was just sent.
 * An older Worker names no status: the neutral line.
 */
const ALREADY_OPEN: Record<string, string> = {
  awaiting_approval: 'A move like this is already waiting for your signature — it is in Approvals.',
  queued: 'A move like this is already queued — the desk is building its card.',
  activating: 'A move like this is already queued — the desk is building its card.',
  expired: 'A move like this is waiting for a signing window — its card is rebuilt when one opens.',
  energizing: 'A move like this was just sent — wait for it to confirm.',
  broadcast: 'A move like this was just sent — wait for it to confirm.',
};

/** The same move already open, as a status (with whose held money it is — the depositor makes the line longer). */
const ALREADY_SHORT: Record<string, string> = {
  awaiting_approval: 'Already waiting for your signature', queued: 'Already queued', activating: 'Already queued',
  expired: 'Waiting for a signing window', energizing: 'Already sent', broadcast: 'Already sent',
};

/** What a short refusal keeps behind Details: where the waiting move stands and where to find it. */
export function moveRefusalMore(err: unknown): string | undefined {
  if (!(err instanceof ApiError) || err.body.reason !== 'move_already_waiting' || typeof err.body.heldFor !== 'string') return undefined;
  return (typeof err.body.status === 'string' ? ALREADY_OPEN[err.body.status] : undefined) ?? ALREADY_OPEN_NEUTRAL;
}

/** Why a move was refused, for the person who asked for it — from the source the press named. */
export function moveRefusal(err: unknown, from: PayoutSource = 'running_hot'): string {
  const word = CLASS_WORD[from] ?? from;
  if (err instanceof ApiError && err.body.reason === 'move_already_waiting') {
    // Out of Flagged, whose held money that waiting move is (db/0103 held_for): a short status line naming them, the
    // way to it behind Details (moveRefusalMore; Screens on d20eb018: the long sentence plus the name was 17 words).
    if (typeof err.body.heldFor === 'string') return `${(typeof err.body.status === 'string' ? ALREADY_SHORT[err.body.status] : undefined) ?? 'Already open'} · held for ${err.body.heldFor}.`;
    return (typeof err.body.status === 'string' ? ALREADY_OPEN[err.body.status] : undefined) ?? ALREADY_OPEN_NEUTRAL;
  }
  if (err instanceof ApiError && err.body.reason === 'insufficient_balance' && typeof err.body.balance === 'string') {
    return `${word} holds ${groupDigits(err.body.balance)} USDT at its last reading — less than this amount. ${FILL[from] ?? FILL.running_hot}`;
  }
  // A cap refusal carries its own line with the figures (contracts capRefusal); it is the sentence.
  if (err instanceof ApiError && typeof err.body.line === 'string' && ['over_move_cap', 'over_day_cap', 'over_first_payout_cap', 'balance_stale', 'balance_unread', 'source_cannot_pay_fee', 'test_amount', 'test_pending', 'test_not_received',
    'held_for_required', 'held_for_unknown', 'nothing_held', 'over_held', 'deal_not_payees', 'deal_voided'].includes(String(err.body.reason))) {
    return err.body.line;
  }
  return pick({ ...MOVE, ...sourced(from) }, err);
}

// ---- the caps ----------------------------------------------------------------

const LIMITS: Record<string, string> = {
  limits_invalid: 'Plain digits, a dot for the decimals, at most six places.',
  limits_not_positive: 'A cap must be above zero.',
  day_below_move: 'The daily cap cannot be below the per-move cap.',
  first_above_move: 'The first-payout cap cannot be above the per-move cap.',
  limits_absurd: 'Over one billion USDT — a typo, not a policy.',
  limits_unset: 'No caps are set yet.',
  source_above_hot: 'A source\'s caps cannot be above Hot\'s.',
  second_device_not_ready: 'The second device cannot be switched on yet: its approval is still a message the Ledger cannot show.',
  fee_floors_invalid: 'Plain digits, a dot for the decimals, at most six places — all three floors.',
  fee_floors_not_positive: 'A floor must be above zero.',
  fee_floors_below_minimum: 'A floor cannot be under what one transfer may burn (15 TRX).',
};

/** Why the caps were not saved. */
export function limitsRefusal(err: unknown): string {
  // Another save came in after the read this one was made from (P-060): the server's own line, nothing written.
  if (limitsChanged(err)) return typeof err.body.line === 'string' ? err.body.line : 'The caps were changed meanwhile. Nothing saved — check them and save again.';
  return pick(LIMITS, err);
}

/** P-060: the 409 limits_changed — another save came in between; `current` is the caps as they stand. */
export function limitsChanged(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 409 && err.body.reason === 'limits_changed';
}

// ---- the approval window ---------------------------------------------------------

const WINDOW: Record<string, string> = {
  minutes_out_of_range: 'A window runs from 1 to 240 minutes.',
};

export function windowRefusal(err: unknown): string {
  return pick(WINDOW, err);
}

// ---- registering a key ------------------------------------------------------------

const KEY: Record<string, string> = {
  first_address_mismatch:
    'The key the Ledger exported does not derive the address the Ledger showed. Nothing was registered — unplug it, plug it back in, and try again; if it happens again, report it.',
  already_registered:
    'This account key is already registered.',
  key_not_this_device:
    'The first address of this account is not the Ledger registered to you. Register the key from your own Ledger, on the account whose first address is your device.',
  no_registered_device:
    'No signing device is registered for you. Ask for your Ledger address to be registered first.',
};

export function keyRefusal(err: unknown): string {
  return pick(KEY, err);
}

// ---- the signature ----------------------------------------------------------------

/**
 * The server would not take the signature — the signing page's sentences
 * (cosign/src/notices.ts signatureRefusal), with the one difference a
 * dashboard has: "the app" is this screen.
 */
export function signatureRefusal(status: number, reason: string | null): string {
  switch (reason) {
    case 'signature_not_authorized':
      return 'The server refused this signature: it was not made by the device registered to you. Check that it is your Ledger.';
    case 'source_needs_own_key':
      return 'The server refused this signature: the money is in a wallet that only its own key can move, and that key is not the one that signed.';
    case 'approval_expired':
      return 'The signing window closed before the signature arrived, so nothing can be sent. The desk builds a fresh transaction.';
    case 'already_cosigned':
      return 'This transaction was already signed, so this signature was not needed.';
    case 'self_approval_refused':
      return 'The server refused this signature: you proposed this transaction, so someone else has to sign it.';
    case 'approval_superseded':
      return 'This transaction was replaced by a newer build while you were signing. Open the newer one from the list.';
    case 'window_closed':
      return 'The approval window closed before the signature arrived, so it was not taken. Open one and sign again.';
    case 'invalid_signature_encoding':
      return 'The server could not read the signature the Ledger returned. Nothing was recorded — report this.';
    default:
      return `The server refused the signature (${status}). Report this rather than trying again.`;
  }
}

/** True when a refusal means the work is already done, not that something is wrong. */
export function refusalIsDone(reason: string | null): boolean {
  return reason === 'already_cosigned' || reason === 'approval_signed';
}

/**
 * What the operator is told once the server took the signature — the page's
 * words (cosign/src/status.ts): the counts decide between "done" and "wait".
 */
export function signedSentence(answer: { signaturesCollected?: unknown; signaturesRequired?: unknown }): string {
  const isCount = (n: unknown): n is number => Number.isInteger(n) && (n as number) >= 1;
  if (!isCount(answer.signaturesCollected) || !isCount(answer.signaturesRequired)) return 'Signed and recorded.';
  const missing = answer.signaturesRequired - answer.signaturesCollected;
  if (missing <= 0) return 'Signed. No other signature is needed: the desk sends it to the network next. Nothing more to do on this device.';
  return `Signed. It needs ${missing} more signature${missing === 1 ? '' : 's'}, from another approver, before it can be sent.`;
}

/**
 * What an UNKNOWN approval outcome means (Screens' rounds 4–5): the device
 * signed, the submission began, and the answer was lost — the attempt may
 * have recorded, and may still be running. The error's own words, then what
 * to do: wait for the list. No cable or device advice (the device did its
 * part), no "approve again" (that would be a second device prompt).
 */
export function approvalUnknownDetail(err: unknown): string {
  const said = err instanceof Error && err.message ? err.message.trim() : String(err ?? '').trim();
  return `${said ? `${said} ` : ''}Your Ledger signed the approval and the desk's answer was lost, so whether it was recorded is not known here. Wait for confirmation: the list is read again, and an approved card shows by whom. The attempt stands for ten minutes; a card still open after that was not recorded.`;
}

/**
 * How the second device's approval ended when the server did not take it
 * (N-068): the headline says the outcome, the tone says whether anything is
 * wrong, `retry` whether pressing again can help — "already approved" is a
 * completion, and the proposer's or the signing device's refusal cannot be
 * retried on this device at all (Screens' round 2, 23 Oct 2026). The detail
 * sits behind Details.
 */
export type ApprovalOutcome = { line: string; tone: 'good' | 'bad' | 'warn'; retry: boolean; detail: string | null };

export function approvalOutcome(err: unknown): ApprovalOutcome {
  const reason = err instanceof ApiError && typeof err.body.reason === 'string' ? err.body.reason : null;
  const said = err instanceof Error && err.message ? err.message : String(err);
  switch (reason) {
    case 'already_approved':
      return { line: 'Already approved — the list refreshes.', tone: 'good', retry: false, detail: 'The other device gave its word first. Nothing more is needed from this one.' };
    case 'self_approval_refused':
      return { line: 'You proposed this payout — the other device approves it.', tone: 'bad', retry: false, detail: 'The second word comes from the Ledger registered to the other operator, never from the one that queued the move.' };
    case 'same_device_refused':
      return { line: 'This Ledger signed it — the other device approves it.', tone: 'bad', retry: false, detail: 'Two devices, not one device twice: the approval comes from the Ledger that did not sign the transaction.' };
    case 'approval_not_needed':
      return { line: 'Only a payout takes a second approval.', tone: 'bad', retry: false, detail: null };
    case 'no_registered_device':
      return { line: 'No signing device is registered for you.', tone: 'bad', retry: false, detail: 'Register your Ledger on Devices & keys first.' };
    case 'signature_not_authorized':
      return { line: 'Not your registered Ledger — use it and approve again.', tone: 'bad', retry: true, detail: 'The signature came from a device that is not the one registered to you.' };
    case 'approval_message_stale':
      return { line: 'The message aged out — approve again.', tone: 'bad', retry: true, detail: 'An approval message stands for ten minutes from the moment it is built.' };
    case 'approval_not_recorded':
      return { line: 'The card changed — refresh and approve again.', tone: 'bad', retry: true, detail: 'The card was signed, approved or replaced while this approval was on its way.' };
    case 'approval_expired':
      return { line: 'The card expired — a fresh one comes next window.', tone: 'bad', retry: false, detail: 'A lapsed card is rebuilt from a fresh block while an approval window is open.' };
    case 'approval_superseded':
      return { line: 'The card was replaced — refresh.', tone: 'bad', retry: false, detail: null };
    case 'window_closed':
      return { line: 'No approval window is open — open one, then approve.', tone: 'bad', retry: true, detail: null };
    default:
      // A reason the screen does not know is the server's decision: not a retry.
      if (reason !== null) return { line: 'Not approved — report this.', tone: 'bad', retry: false, detail: `${said} (${reason})` };
      // The server answered without a reason (a 5xx, a gateway): not a decision — and not evidence that nothing was
      // recorded, either (Screens' round 4). The screen settles it by reading the list again (lib/custody-screen.ts
      // approvalEndOf); this wording stands only where that is not possible.
      if (err instanceof ApiError) {
        return { line: `Not confirmed — the server answered ${err.status}.`, tone: 'warn', retry: false, detail: `${said} Whether the approval was recorded is not known here: check the list before approving again.` };
      }
      // Not the server: the cable, the device or this build, BEFORE anything was signed. Its own words and the way out sit behind Details (Screens' round 3: the headline fits one line).
      const local = err as { remedy?: unknown; retry?: unknown } | null;
      const remedy = typeof local?.remedy === 'string' && local.remedy ? local.remedy
        : 'Check the cable, that the TRON app is open on the Ledger and Ledger Live is closed, then approve again.';
      return { line: 'Not approved — nothing signed, nothing recorded.', tone: 'bad', retry: local?.retry !== false, detail: `${said} ${remedy}` };
  }
}


// ---- releasing a Merchant Hot deposit to Hot (N-099, his "yes ofc", 2 Nov 2026) -------------------------------------------

const RELEASE: Record<string, string> = {
  deposit_id_invalid: 'That deposit number is not one the desk knows.',
  request_id_invalid: 'The press could not be sent — press Release again.',
  not_found: 'That deposit is not on the desk.',
  no_open_window: 'Open the desk window first, then press Release.',
  not_merchant_hot: 'Only a deposit KYT sent to Merchant Hot can be released.',
  not_swept_yet: 'Not in Merchant Hot yet — release it once its sweep lands.',
  release_pending: 'A release for this deposit is already under way.',
  already_released: 'Already released to Hot.',
  request_voided: 'That press was set aside — press Release again.',
  source_cannot_pay_fee: 'Merchant Hot is short of TRX for the fee — refill it from Fee first.',
  balance_unread: 'Merchant Hot was not read — try again in a minute.',
  balance_stale: 'Merchant Hot\'s last reading is too old — try again in a minute.',
  insufficient_balance: 'Merchant Hot holds less than this deposit.',
  over_move_cap: 'Over the per-move cap.',
  over_day_cap: 'Over today\'s cap.',
  no_source_wallet: 'Merchant Hot is not registered on this chain.',
  no_destination_wallet: 'Hot is not registered on this chain.',
  limits_unset: 'No caps are set yet — set them in Settings.',
  asset_not_supported: 'This deposit\'s asset cannot be released.',
};

/** Why a release was refused: the desk's own line when it sends one, else these words for its reason; an unknown reason as it came. */
export function releaseRefusal(err: unknown): string {
  if (err instanceof ApiError && typeof err.body.line === 'string' && err.body.line) return err.body.line;
  // Short of money: the figure the desk read, and when (its balance and balanceAsOf).
  if (err instanceof ApiError && err.body.reason === 'insufficient_balance' && typeof err.body.balance === 'string') {
    const asOf = typeof err.body.balanceAsOf === 'string' ? ` (read ${fmtTime(err.body.balanceAsOf)})` : '';
    return `Merchant Hot holds ${groupDigits(err.body.balance)} USDT·TRC${asOf}.`;
  }
  return pick(RELEASE, err);
}
